import { Injectable, Inject } from '@angular/core'
import { HttpClient, HttpRequest, HttpParams, HttpHeaders } from '@angular/common/http'
import { AngularFireAuth } from 'angularfire2/auth'

import { Secure } from './secure'
import { FIREBASE_SETTINGS } from './firebase.settings.token'

export interface EncryptInstructions {
  params?: string[] | boolean,
  headers?: string[] | boolean,
  body?: string[] | boolean
  response?: string[] | boolean
}

@Injectable()
export class ApiService {
  static secure: Secure

  static authHttpFactory (
    auth: AngularFireAuth,
    @Inject(FIREBASE_SETTINGS) firebaseSettings: any
  ) {
    return {
      tokenGetter: async () => await auth.auth.currentUser?auth.auth.currentUser.getIdToken():null,
      whitelistedDomains: [
        firebaseSettings.functionsDomain,
      ]
    }
  }

  static initializeSecure (key: string, iv: string, pass?: string, secret?: string): Secure {
    return ApiService.secure = new Secure(key, iv, pass, secret)
  }

  constructor(
    private http: HttpClient,
    @Inject(FIREBASE_SETTINGS) private firebaseSettings: any
  ) { }

  requestEncrypted<T = any> (request: HttpRequest<T | any>, encrypt?: EncryptInstructions) {
    const encrypted: any = {}
    if (ApiService.secure === undefined) {
      return this.http.request(request.method, request.url, request)
    } else {
      if (encrypt) {
        if (encrypt.body && request.body) {
          if (typeof encrypt.body === 'boolean') {
            encrypted.body = ApiService.secure.encrypt(JSON.stringify(request.body))
          } else {
            encrypted.body = Object.assign.apply(
              {},
              [request.body].concat(
                encrypt.body.map(key =>
                  ({ [key]: ApiService.secure.encrypt(request.body[key]) })
                )
              )
            )
          }
        }
        if (encrypt.params && request.params) {
          if (typeof encrypt.params === 'boolean') {
            encrypted.params = ApiService.secure.encrypt(JSON.stringify(request.params))
          } else {
            encrypted.params = Object.assign.apply(
              {},
              [request.params].concat(
                encrypt.params.map(key =>
                  ({ [key]: ApiService.secure.encrypt(request.params.get(key)) } as HttpParams)
                )
              )
            )
          }
        }
        if (encrypt.headers && request.headers) {
          if (typeof encrypt.headers === 'boolean') {
            encrypted.headers = ApiService.secure.encrypt(JSON.stringify(request.headers))
          } else {
            encrypted.headers = Object.assign.apply(
              {},
              [request.headers].concat(
                encrypt.headers.map(key =>
                  ({ [key]: ApiService.secure.encrypt(request.headers.get(key)) } as HttpHeaders)
                )
              )
            )
          }
        }
      }
      return this.http.request(request.method, request.url, Object.assign({}, request, encrypted))
        .map(response => {
          if (encrypt && encrypt.response && response) {
            if (typeof encrypt.response === 'boolean') {
              return ApiService.secure.decrypt(String.fromCharCode.apply(null, response))
            } else {
              response = response.byteLength ? JSON.parse(String.fromCharCode.apply(null, response)) : response
              return Object.assign.apply(
                {},
                [].concat(
                  encrypt.response.map(key =>
                    (response[key] ? { [key]: ApiService.secure.decrypt(response[key]) } : {}) as HttpParams
                  )
                )
              )
            }
          } else {
            return response
          }
        })
    }
  }

  private wrapEncrypted<T> (method: string, path: string, request: HttpRequest<T> | any, encrypt?: EncryptInstructions) {
    return this.requestEncrypted(Object.assign({}, request, { url: this.url(path), method: method }), encrypt)
  }

  getEncrypted (path: string, request: HttpRequest<null> | any, encrypt?: EncryptInstructions) {
    return this.wrapEncrypted('GET', path, request, encrypt)
  }

  deleteEncrypted (path: string, request: HttpRequest<null> | any, encrypt?: EncryptInstructions) {
    return this.wrapEncrypted('DELETE', path, request, encrypt)
  }

  headEncrypted (path: string, request: HttpRequest<null> | any, encrypt?: EncryptInstructions) {
    return this.wrapEncrypted('HEAD', path, request, encrypt)
  }

  optionsEncrypted (path: string, request: HttpRequest<null> | any, encrypt?: EncryptInstructions) {
    return this.wrapEncrypted('OPTIONS', path, request, encrypt)
  }

  postEncrypted<T> (path: string, request: HttpRequest<T> | any, encrypt?: EncryptInstructions) {
    return this.wrapEncrypted('POST', path, request, encrypt)
  }

  postRequest<T> (path: string, request: HttpRequest<T> | any) {
    return this.wrapEncrypted('POST', path, request)
  }

  putEncrypted<T> (path: string, request: HttpRequest<T> | any, encrypt?: EncryptInstructions) {
    return this.wrapEncrypted('PUT', path, request, encrypt)
  }

  patchEncrypted<T> (path: string, request: HttpRequest<T> | any, encrypt?: EncryptInstructions) {
    return this.wrapEncrypted('PATCH', path, request, encrypt)
  }

  url (path: string) {
    return `${this.firebaseSettings.functionsURL}${path[0] === '/' ? '' : '/'}${path}`
  }
}
